/** The draw-mode elevation profile: an SVG strip showing the route's
 * elevation curve plus a grade-colored band beneath it — or a slim status
 * message while terrain sampling is still working, or came back empty.
 * Before the `sampling` prop existed, "still computing" and "genuinely no
 * data here" both rendered as identical silence (absent stats), which the
 * UX review flagged as reading like breakage — see
 * docs/WEB_PLANNER_UX_REVIEW.md P1 #4. Pure — driven entirely by the
 * `samples`/`sampling` props (plus the optional `dayMarks`/`onScrub` below);
 * App.tsx owns debouncing/aborting the terrain sampling itself, this just
 * draws whatever it's handed (or says why there's nothing to draw yet). */
import { useState } from "react";
import type { PointerEvent } from "react";
import { formatFeet, formatMiles, gainLossM } from "../plan/geo";
import type { ElevationSample } from "../plan/types";
import type { SamplingState } from "../App";

const VIEW_W = 560;
const VIEW_H = 90;
const CHART_TOP = 4;
const CHART_BOTTOM = 54;
const BAND_TOP = 62;
const BAND_H = 8;
const LABEL_Y = 86;
const SEGMENTS = 60;

interface GradeSegment {
  /** Percent (6 means 6%), or null when there's no usable sample pair. */
  gradePct: number | null;
}

/** The phone's EXACT rule: slice the route into SEGMENTS equal-distance
 * pieces; each piece's grade compares the samples NEAREST its two boundary
 * distances (nearest-neighbor, not interpolated) rather than the nominal
 * segment width — a sparse sample set near a boundary can land both
 * lookups on the same sample, which is the "no-data" case below. */
function gradeSegments(samples: ElevationSample[]): GradeSegment[] {
  const start = samples[0].distM;
  const total = samples[samples.length - 1].distM - start;
  if (total <= 0) return Array.from({ length: SEGMENTS }, () => ({ gradePct: null }));

  const nearest = (d: number): ElevationSample => {
    let best = samples[0];
    let bestDelta = Math.abs(best.distM - d);
    for (const s of samples) {
      const delta = Math.abs(s.distM - d);
      if (delta < bestDelta) {
        best = s;
        bestDelta = delta;
      }
    }
    return best;
  };

  return Array.from({ length: SEGMENTS }, (_, k) => {
    const a = nearest(start + (total * k) / SEGMENTS);
    const b = nearest(start + (total * (k + 1)) / SEGMENTS);
    const dDist = Math.abs(b.distM - a.distM);
    return { gradePct: dDist > 0 ? (Math.abs(b.eleM - a.eleM) / dDist) * 100 : null };
  });
}

/** Grade-band colors are CSS custom properties (theme.css), with night
 * overrides — the no-data band in particular was nearly invisible at night
 * back when these were light-theme literals. Read through inline `style`
 * rather than the SVG `fill` attribute: attribute-position var() support is
 * inconsistent across engines, the style attribute resolves custom
 * properties reliably everywhere. */
function segmentColor({ gradePct }: GradeSegment): string {
  if (gradePct === null) return "var(--grade-nodata)";
  if (gradePct < 6) return "var(--grade-easy)";
  if (gradePct < 12) return "var(--grade-moderate)";
  return "var(--grade-steep)";
}

/** "Elevation profile, 490 ft gain over 3.2 mi" — the chart's aria-label,
 * derived from the real samples rather than a static "Elevation profile"
 * (Aug 27 accessibility pass). formatFeet, not formatGainFt: the latter's
 * leading "+" reads oddly spoken aloud ("plus 490 feet"), and the sign is
 * redundant next to the word "gain" anyway. Exported/pure/tested the same
 * way SearchPalette's search()/normalize() are. */
export function elevationProfileSummary(samples: ElevationSample[]): string {
  if (samples.length < 2) return "Elevation profile";
  const { gainM } = gainLossM(samples);
  const totalDistM = samples[samples.length - 1].distM;
  return `Elevation profile, ${formatFeet(gainM)} gain over ${formatMiles(totalDistM)}`;
}

export default function ProfileStrip({
  samples,
  sampling,
  dayMarks = [],
  onScrub,
}: {
  samples: ElevationSample[];
  sampling: SamplingState;
  /** Vertical reference lines for each overnight — distance along the same
   * flattened geometry `samples` is sampled from. */
  dayMarks?: { distM: number; label: string }[];
  /** Fired with the nearest sample under the pointer on every hover move,
   * and with null on pointerleave — App.tsx's future wave mirrors this
   * onto the map's plan-scrub layer. */
  onScrub?: (s: ElevationSample | null) => void;
}) {
  // Hook order must stay unconditional — this runs every render, even
  // through the early-return status states below, so React never loses
  // track of it.
  const [cursor, setCursor] = useState<ElevationSample | null>(null);

  // "Still working" and "tried, got nothing" both need their own words —
  // rendering nothing (the old behavior) reads identically to "broken."
  if (sampling === "pending") {
    return <div className="profile-strip profile-strip-message">SAMPLING ELEVATION…</div>;
  }
  if (sampling === "unavailable") {
    return <div className="profile-strip profile-strip-message">NO ELEVATION DATA HERE</div>;
  }
  if (samples.length < 2) return null; // idle — nothing to plot yet

  const eles = samples.map((s) => s.eleM);
  const minEle = Math.min(...eles);
  const maxEle = Math.max(...eles);
  const eleSpan = maxEle - minEle || 1; // a dead-flat profile still needs a line, not a /0
  const totalDist = samples[samples.length - 1].distM || 1;

  const xFor = (distM: number) => (distM / totalDist) * VIEW_W;
  const yFor = (eleM: number) =>
    CHART_BOTTOM - ((eleM - minEle) / eleSpan) * (CHART_BOTTOM - CHART_TOP);

  const linePoints = samples.map((s) => `${xFor(s.distM)},${yFor(s.eleM)}`).join(" ");
  const baseline = CHART_BOTTOM;
  const fillPoints = `${xFor(samples[0].distM)},${baseline} ${linePoints} ${xFor(
    samples[samples.length - 1].distM,
  )},${baseline}`;

  const segments = gradeSegments(samples);
  const segW = VIEW_W / SEGMENTS;

  // Nearest-neighbor by distM — the same rule gradeSegments' own `nearest`
  // uses above, so the scrub cursor and the grade band agree on what
  // "closest sample" means.
  function nearestSample(distM: number): ElevationSample {
    let best = samples[0];
    let bestDelta = Math.abs(best.distM - distM);
    for (const s of samples) {
      const delta = Math.abs(s.distM - distM);
      if (delta < bestDelta) {
        best = s;
        bestDelta = delta;
      }
    }
    return best;
  }

  // preserveAspectRatio="none" makes the viewBox<->screen mapping linear,
  // so a plain fraction-of-width conversion lands exactly on the right
  // viewBox x — no need to account for letterboxing.
  function handlePointerMove(e: PointerEvent<SVGSVGElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    const fracX = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const sample = nearestSample(fracX * totalDist);
    setCursor(sample);
    onScrub?.(sample);
  }

  function handlePointerLeave() {
    setCursor(null);
    onScrub?.(null);
  }

  const cursorX = cursor ? xFor(cursor.distM) : 0;
  const cursorFlipped = cursorX > VIEW_W / 2;

  return (
    <svg
      className="profile-strip"
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={elevationProfileSummary(samples)}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
    >
      <polygon points={fillPoints} className="profile-strip-fill" />
      {dayMarks.map((mark, i) => (
        <g key={i}>
          <line
            x1={xFor(mark.distM)}
            y1={CHART_TOP}
            x2={xFor(mark.distM)}
            y2={BAND_TOP + BAND_H}
            style={{ stroke: "var(--soilink)", strokeWidth: 1, strokeDasharray: "2 2", opacity: 0.4 }}
          />
          <text
            x={xFor(mark.distM)}
            y={CHART_TOP + 8}
            textAnchor="middle"
            className="profile-strip-label"
          >
            {mark.label}
          </text>
        </g>
      ))}
      <polyline points={linePoints} className="profile-strip-line" />
      {segments.map((seg, i) => (
        <rect
          key={i}
          x={i * segW}
          y={BAND_TOP}
          width={segW}
          height={BAND_H}
          style={{ fill: segmentColor(seg) }}
        />
      ))}
      <text x={4} y={LABEL_Y} className="profile-strip-label">
        MIN {formatFeet(minEle)}
      </text>
      <text x={VIEW_W - 4} y={LABEL_Y} className="profile-strip-label profile-strip-label-right">
        MAX {formatFeet(maxEle)}
      </text>
      {cursor && (
        <g>
          <line
            x1={cursorX}
            y1={CHART_TOP}
            x2={cursorX}
            y2={BAND_TOP + BAND_H}
            style={{ stroke: "var(--trace)", strokeWidth: 1.25 }}
          />
          <circle
            cx={cursorX}
            cy={yFor(cursor.eleM)}
            r={3}
            style={{ fill: "var(--trace)", stroke: "var(--sheet-bg)", strokeWidth: 1.5 }}
          />
          <text
            x={cursorFlipped ? cursorX - 5 : cursorX + 5}
            y={CHART_TOP + 8}
            className={`profile-strip-label${cursorFlipped ? " profile-strip-label-right" : ""}`}
          >
            {formatMiles(cursor.distM)} · {formatFeet(cursor.eleM)}
          </text>
        </g>
      )}
    </svg>
  );
}
